import React, { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from '../hooks/use-i18n'
import { getCountries, getCitiesByCountry, updatePublicParticipantPreferences, enrollPublicParticipantBulk } from '../services/publicService'
import type { PublicParticipant, CountryItem, CityItem, PublicTournament } from '../types/public'
import { LocationFieldButton, SelectPanel } from './SearchableSelect'

function leagueDisplayName(name: string): string {
  if (!name) return name
  const prefix = 'Public Football – '
  return name.startsWith(prefix) ? name.slice(prefix.length) : name
}

interface EditPublicLocationModalProps {
  isOpen: boolean
  onClose: () => void
  participant: PublicParticipant | null
  /** All active football tournaments (not only currently enrolled). */
  leagues?: PublicTournament[]
  /** Leagues this participant is enrolled in. */
  enrolledTournamentIds?: string[]
  onSaved: (savedLeagueIds?: string[]) => void | Promise<void>
}

const EditPublicLocationModal: React.FC<EditPublicLocationModalProps> = ({
  isOpen,
  onClose,
  participant,
  leagues = [],
  enrolledTournamentIds = [],
  onSaved,
}) => {
  const { t } = useTranslation()
  const [countries, setCountries] = useState<CountryItem[]>([])
  const [cities, setCities] = useState<CityItem[]>([])
  const [loadingCities, setLoadingCities] = useState(false)
  const [countryCode, setCountryCode] = useState('')
  const [city, setCity] = useState('')
  const [enrolledIds, setEnrolledIds] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [openPicker, setOpenPicker] = useState<'country' | 'city' | null>(null)
  const [pickerQuery, setPickerQuery] = useState('')
  const didInitOpen = useRef(false)

  useEffect(() => {
    if (!isOpen) return
    getCountries().then(setCountries).catch(() => setCountries([]))
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      didInitOpen.current = false
      setOpenPicker(null)
      setPickerQuery('')
      return
    }
    if (!participant || didInitOpen.current) return
    didInitOpen.current = true
    setCountryCode(participant.countryCode || '')
    setCity(participant.city ?? '')
    const activeIds = new Set(leagues.map((l) => l.id))
    const fromFavorites = (participant.favoriteTournamentIds ?? []).filter((id) => activeIds.has(id))
    const fromEnrolled = enrolledTournamentIds.filter((id) => activeIds.has(id))
    setEnrolledIds(fromFavorites.length > 0 ? fromFavorites : fromEnrolled)
  }, [isOpen, participant, leagues, enrolledTournamentIds])

  useEffect(() => {
    if (!countryCode.trim()) {
      setCities([])
      return
    }
    let cancelled = false
    setLoadingCities(true)
    getCitiesByCountry(countryCode)
      .then((list) => { if (!cancelled) setCities(list) })
      .catch(() => { if (!cancelled) setCities([]) })
      .finally(() => { if (!cancelled) setLoadingCities(false) })
    return () => { cancelled = true }
  }, [countryCode])

  const toggleEnrolled = (id: string) => {
    setEnrolledIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const handleSave = async () => {
    if (!participant) return
    if (!countryCode.trim() || !city.trim()) {
      toast.error(t('playPublic.country') + ' / ' + t('playPublic.city') + ' required')
      return
    }
    if (leagues.length > 0 && enrolledIds.length === 0) {
      toast.error(t('publicFootball.enrolledLeaguesNeedOne'))
      return
    }
    const cc = countryCode.trim().toUpperCase()
    const cityTrimmed = city.trim()
    setLoading(true)
    try {
      if (leagues.length > 0) {
        await enrollPublicParticipantBulk({
          tournamentIds: enrolledIds,
          countryCode: cc,
          city: cityTrimmed,
        })
      }
      await updatePublicParticipantPreferences(participant.id, {
        countryCode: cc,
        city: cityTrimmed,
        favoriteTournamentIds: leagues.length > 0 ? enrolledIds : undefined,
      })
      await onSaved(enrolledIds)
      onClose()
      toast.success(t('common.save'))
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Failed to update preferences')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  const countryItems = countries.map((c) => ({ value: c.code, label: c.name }))
  const cityItems = cities.map((c) => ({ value: c.cityName, label: c.cityName }))
  const selectedCountryLabel = countries.find((c) => c.code === countryCode)?.name || ''

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-2xl shadow-xl max-w-md w-full h-[min(36rem,calc(100vh-2rem))] max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {openPicker ? (
          <SelectPanel
            title={openPicker === 'country' ? t('playPublic.country') : t('playPublic.city')}
            items={openPicker === 'country' ? countryItems : cityItems}
            value={openPicker === 'country' ? countryCode : city}
            query={pickerQuery}
            onQueryChange={setPickerQuery}
            placeholder={
              openPicker === 'country' ? t('playPublic.selectCountry') : t('playPublic.selectCity')
            }
            onBack={() => {
              setOpenPicker(null)
              setPickerQuery('')
            }}
            onSelect={(value) => {
              if (openPicker === 'country') {
                setCountryCode(value)
                setCity('')
              } else {
                setCity(value)
              }
              setOpenPicker(null)
              setPickerQuery('')
            }}
          />
        ) : (
          <div className="flex flex-col flex-1 min-h-0 overflow-y-auto p-6">
        <h2 className="text-lg font-semibold text-foreground mb-2">{t('publicFootball.editPreferences')}</h2>
        <p className="text-sm text-muted-foreground mb-4">{t('publicFootball.editPreferencesDesc')}</p>
        <div className="space-y-4">
          <LocationFieldButton
            label={`${t('playPublic.country')} *`}
            value={selectedCountryLabel}
            placeholder={t('playPublic.selectCountry')}
            onOpen={() => {
              setPickerQuery('')
              setOpenPicker('country')
            }}
          />
          {loadingCities ? (
            <p className="text-sm text-muted-foreground py-2">{t('common.loading')}</p>
          ) : (
            <LocationFieldButton
              label={`${t('playPublic.city')} *`}
              value={city}
              placeholder={countryCode ? t('playPublic.selectCity') : t('playPublic.selectCountryFirst')}
              disabled={!countryCode}
              onOpen={() => {
                setPickerQuery('')
                setOpenPicker('city')
              }}
            />
          )}
          {leagues.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">{t('publicFootball.favoriteLeagues')}</label>
              <p className="text-xs text-muted-foreground mb-2">{t('publicFootball.favoriteLeaguesHint')}</p>
              <div className="flex flex-wrap gap-2">
                {leagues.map((l) => (
                  <label key={l.id} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-muted/20 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enrolledIds.includes(l.id)}
                      onChange={() => toggleEnrolled(l.id)}
                      className="rounded"
                    />
                    <span className="text-sm font-medium">{leagueDisplayName(l.name)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl text-muted-foreground hover:bg-muted">
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || !countryCode.trim() || !city.trim() || (leagues.length > 0 && enrolledIds.length === 0)}
            className="px-4 py-2 rounded-xl bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {loading ? '...' : t('common.save')}
          </button>
        </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default EditPublicLocationModal
